// The data model catalog (docs/catalog.md): every class the tenant's Vaults have reported, with a display name and
// metadata the admins give it, and a status that tells new data models from those already looked at. Pure helpers;
// the React side (loading, the names registry) is lib/catalogs.tsx.

export type CatalogStatus = 'new' | 'checked' | 'deprecated';
export type Sensitivity = 'none' | 'internal' | 'confidential' | 'personal';
export const SENSITIVITIES: Sensitivity[] = ['none', 'internal', 'confidential', 'personal'];

export interface CatalogEntry {
  class: string; displayName: string; displayPlural: string;
  description: string | null; domain: string | null; tags: string[]; steward: string | null; sensitivity: Sensitivity | null;
  status: CatalogStatus; firstSeenAt: number; reviewedAt: number | null; reviewedBy: string | null;
  updatedAt: number | null; updatedBy: string | null;
  /** The system an admin pinned the class to (wins over what the data says), or null. */
  system?: string | null;
  /** The names the class name alone gives. */
  derived: { displayName: string; displayPlural: string };
  /** Whether the environment looked at has this class (the list response only). */
  inEnvironment?: boolean;
  /** The environments whose Vaults have reported the class (the list response only). */
  environments?: string[];
  /** What the automatic categorization suggests where the admins have not set a value; null = nothing. */
  suggestions?: CatalogSuggestions | null;
}

/** What a suggestion can be accepted for. */
export type SuggestionPart = 'domain' | 'sensitivity' | 'tags';

/** The suggestions of a class (cloud/src/classify.ts): only for what the admins have not set. */
export interface CatalogSuggestions {
  domain: string | null; sensitivity: Sensitivity | null; tags: string[];
  /** Why, in words, per part. */
  reasons: { domain: string[]; sensitivity: string[]; tags: string[] };
  /** 0..1 */
  confidence: { domain: number | null; sensitivity: number | null };
  /** The fields that made the sensitivity what it is. */
  fields: { personal: string[]; confidential: string[] };
  at: number;
}

export interface CatalogData { classes: CatalogEntry[]; domains: string[]; tags: string[] }

export const STATUS_LABEL: Record<CatalogStatus, string> = { new: 'New', checked: 'Checked', deprecated: 'Deprecated' };
export const SENSITIVITY_LABEL: Record<Sensitivity, string> = { none: 'Not sensitive', internal: 'Internal', confidential: 'Confidential', personal: 'Personal data' };

/** The limits the Cloud checks (cloud/src/server.ts catalogPatch). */
export const CATALOG_LIMITS = { name: 80, description: 2000, domain: 60, tags: 20, tag: 40, steward: 120 };

/** A class the catalog does not have yet (it has just arrived) counts as new. */
export const statusOf = (catalog: ReadonlyMap<string, CatalogEntry>, cls: string): CatalogStatus => catalog.get(cls)?.status ?? 'new';

/** How many of the classes are in each status (all = every class). */
export function statusCounts(classes: readonly string[], catalog: ReadonlyMap<string, CatalogEntry>): Record<CatalogStatus | 'all', number> {
  const out = { all: classes.length, new: 0, checked: 0, deprecated: 0 };
  for (const c of classes) out[statusOf(catalog, c)]++;
  return out;
}

/** The classes still to check, in the order given. */
export const newClasses = (classes: readonly string[], catalog: ReadonlyMap<string, CatalogEntry>) => classes.filter((c) => statusOf(catalog, c) === 'new');

/** A value as shown: set by an admin, or only suggested. */
export interface Shown<T> { value: T; suggested: boolean }

/** The domain of a class: the admins' one, else the suggested one, else null. */
export function domainOf(e: CatalogEntry | undefined): Shown<string> | null {
  if (e?.domain) return { value: e.domain, suggested: false };
  return e?.suggestions?.domain ? { value: e.suggestions.domain, suggested: true } : null;
}

/** The sensitivity of a class: the admins' one, else the suggested one, else null. */
export function sensitivityOf(e: CatalogEntry | undefined): Shown<Sensitivity> | null {
  if (e?.sensitivity) return { value: e.sensitivity, suggested: false };
  return e?.suggestions?.sensitivity ? { value: e.suggestions.sensitivity, suggested: true } : null;
}

/** Whether anything is suggested for the class. */
export const hasSuggestions = (e: CatalogEntry | undefined): boolean =>
  !!e?.suggestions && (!!e.suggestions.domain || !!e.suggestions.sensitivity || e.suggestions.tags.length > 0);

/** How sure a suggestion is, in a word. */
export const confidenceWord = (c: number | null | undefined): string => ((c ?? 0) >= 0.75 ? 'likely' : 'possible');

/** The tooltip of a suggested value: how sure and why. */
export function suggestionTitle(s: CatalogSuggestions, part: SuggestionPart): string {
  const c = part === 'tags' ? null : s.confidence[part];
  const head = part === 'tags' ? 'Suggested tags' : `Suggested (${confidenceWord(c)}, ${Math.round((c ?? 0) * 100)} %)`;
  return [head, ...s.reasons[part].map((r) => `· ${r}`)].join('\n');
}

/** The domains of the classes (the admins' or suggested), sorted. */
export function domainsOf(classes: readonly string[], catalog: ReadonlyMap<string, CatalogEntry>): string[] {
  return [...new Set(classes.map((c) => domainOf(catalog.get(c))?.value).filter((d): d is string => !!d))].sort((a, b) => a.localeCompare(b));
}

// ---------------------------------------------------------------- facets of the class list

/** What the class list can be narrowed by besides the status: chips, several values per facet. */
export type Facet = 'domain' | 'sens' | 'sys';
export const FACETS: Facet[] = ['domain', 'sens', 'sys'];
/** The values chosen per facet: a class shows when it has one of the values of each facet that has any (OR within, AND across). */
export type FacetSelection = Partial<Record<Facet, readonly string[]>>;
/** A value of a facet with the classes it would show (given the other facets), and how many of them only by a suggestion. */
export interface FacetValue { key: string; count: number; suggested: number }

/** The value of a class for a facet (NO_GROUP = none: no domain, sensitivity not set, no system) and whether only suggested. */
export function facetValueOf(facet: Facet, e: CatalogEntry | undefined, system: string | null): Shown<string> {
  const v = facet === 'domain' ? domainOf(e) : facet === 'sens' ? sensitivityOf(e) : system ? { value: system, suggested: false } : null;
  return v ?? { value: NO_GROUP, suggested: false };
}

const active = (sel: FacetSelection | undefined, f: Facet) => (sel?.[f]?.length ? sel[f]! : null);

/** Whether a class is shown by the facets chosen, leaving out one facet (for the counts of that facet). */
function matchesFacets(cls: string, catalog: ReadonlyMap<string, CatalogEntry>, sel: FacetSelection | undefined, systemOfClass: (cls: string) => string | null, except?: Facet): boolean {
  for (const f of FACETS) {
    const values = f === except ? null : active(sel, f);
    if (values && !values.includes(facetValueOf(f, catalog.get(cls), systemOfClass(cls)).value)) return false;
  }
  return true;
}

/**
 * The values of each facet with their counts, as faceted search does: a facet's counts are of the classes of the status
 * that the OTHER facets let through, so a count says what choosing (adding) that value would show. Domains and systems:
 * most classes first (then A–Z); sensitivity: personal first; the "none" value (NO_GROUP) last. Chosen values are always
 * there (count 0 when nothing has them), others only with a count.
 */
export function classFacets(classes: readonly string[], catalog: ReadonlyMap<string, CatalogEntry>, status: CatalogStatus | 'all', sel: FacetSelection = {},
  systemOfClass: (cls: string) => string | null = () => null, label: (facet: Facet, key: string) => string = (_f, k) => k): Record<Facet, FacetValue[]> {
  const out = {} as Record<Facet, FacetValue[]>;
  const ofStatus = classes.filter((c) => status === 'all' || statusOf(catalog, c) === status);
  for (const f of FACETS) {
    const values = new Map<string, FacetValue>();
    for (const k of sel[f] ?? []) values.set(k, { key: k, count: 0, suggested: 0 });
    for (const c of ofStatus) {
      if (!matchesFacets(c, catalog, sel, systemOfClass, f)) continue;
      const v = facetValueOf(f, catalog.get(c), systemOfClass(c));
      const x = values.get(v.value) ?? (values.set(v.value, { key: v.value, count: 0, suggested: 0 }), values.get(v.value)!);
      x.count++;
      if (v.suggested) x.suggested++;
    }
    const none = (x: FacetValue) => (x.key === NO_GROUP ? 1 : 0);
    out[f] = [...values.values()].sort(f === 'sens'
      ? (a, b) => none(a) - none(b) || SENSITIVITY_ORDER.indexOf(a.key as Sensitivity) - SENSITIVITY_ORDER.indexOf(b.key as Sensitivity)
      : (a, b) => none(a) - none(b) || b.count - a.count || label(f, a.key).localeCompare(label(f, b.key)));
  }
  return out;
}

/** Whether any facet has a value chosen. */
export const hasFacets = (sel: FacetSelection | undefined): boolean => FACETS.some((f) => !!active(sel, f));

/**
 * The class list as shown: only those of the status and facets asked for (a string = one domain, as older callers give),
 * deprecated ones last; the order given stays otherwise.
 */
export function listClasses<T extends { name: string }>(classes: readonly T[], catalog: ReadonlyMap<string, CatalogEntry>, status: CatalogStatus | 'all',
  facets?: FacetSelection | string, systemOfClass: (cls: string) => string | null = () => null): T[] {
  const sel: FacetSelection = typeof facets === 'string' ? { domain: [facets] } : facets ?? {};
  const shown = classes.filter((c) => (status === 'all' || statusOf(catalog, c.name) === status) && matchesFacets(c.name, catalog, sel, systemOfClass));
  const last = (c: T) => (statusOf(catalog, c.name) === 'deprecated' ? 1 : 0);
  return shown.map((c, i) => ({ c, i })).sort((a, b) => last(a.c) - last(b.c) || a.i - b.i).map((x) => x.c);
}

// ---------------------------------------------------------------- grouping the class list

export type GroupBy = 'none' | 'domain' | 'system' | 'sensitivity';
export const GROUP_BYS: GroupBy[] = ['none', 'domain', 'system', 'sensitivity'];
export const GROUP_LABEL: Record<GroupBy, string> = { none: 'None', domain: 'Domain', system: 'System', sensitivity: 'Sensitivity' };

/** A group of the class list: its key (in the URL when folded), what it says, the classes, how many of them only by a suggestion. */
export interface ClassGroup<T> { key: string; label: string; items: T[]; suggested: number }

/** The key of the group of classes without a value. */
export const NO_GROUP = '-';
const SENSITIVITY_ORDER: Sensitivity[] = ['personal', 'confidential', 'internal', 'none'];

/**
 * The classes in groups, in the order given within each: by domain (A–Z, "No domain" last), system (by name, "No system"
 * last) or sensitivity (personal first, "Not set" last). A suggested value groups like a set one (counted in `suggested`).
 */
export function groupClasses<T extends { name: string }>(classes: readonly T[], catalog: ReadonlyMap<string, CatalogEntry>, by: GroupBy,
  systemOfClass: (cls: string) => string | null = () => null, systemLabel: (code: string) => string = (c) => c): ClassGroup<T>[] {
  if (by === 'none') return [{ key: NO_GROUP, label: '', items: [...classes], suggested: 0 }];
  const groups = new Map<string, ClassGroup<T>>();
  for (const c of classes) {
    const e = catalog.get(c.name);
    let v: Shown<string> | null;
    if (by === 'domain') v = domainOf(e);
    else if (by === 'sensitivity') v = sensitivityOf(e);
    else { const s = systemOfClass(c.name); v = s ? { value: s, suggested: false } : null; }
    const key = v?.value ?? NO_GROUP;
    const label = !v ? (by === 'domain' ? 'No domain' : by === 'system' ? 'No system' : 'Not set')
      : by === 'sensitivity' ? SENSITIVITY_LABEL[v.value as Sensitivity] : by === 'system' ? systemLabel(v.value) : v.value;
    const g = groups.get(key) ?? (groups.set(key, { key, label, items: [], suggested: 0 }), groups.get(key)!);
    g.items.push(c);
    if (v?.suggested) g.suggested++;
  }
  const rank = (g: ClassGroup<T>) => (g.key === NO_GROUP ? 1 : 0);
  const order = by === 'sensitivity'
    ? (a: ClassGroup<T>, b: ClassGroup<T>) => rank(a) - rank(b) || SENSITIVITY_ORDER.indexOf(a.key as Sensitivity) - SENSITIVITY_ORDER.indexOf(b.key as Sensitivity)
    : (a: ClassGroup<T>, b: ClassGroup<T>) => rank(a) - rank(b) || a.label.localeCompare(b.label);
  return [...groups.values()].sort(order);
}

/** Tags typed as text: split at commas, trimmed, no empties, no duplicates (case-insensitively), at most the limit. */
export function parseTags(text: string, existing: readonly string[] = []): string[] {
  const out = [...existing];
  for (const raw of text.split(',')) {
    const t = raw.trim().slice(0, CATALOG_LIMITS.tag);
    if (t && !out.some((x) => x.toLowerCase() === t.toLowerCase())) out.push(t);
  }
  return out.slice(0, CATALOG_LIMITS.tags);
}

/** Suggestions for what is typed: those that contain it (starting with it first), not the ones already chosen. */
export function suggestions(all: readonly string[], typed: string, chosen: readonly string[] = [], max = 8): string[] {
  const q = typed.trim().toLowerCase();
  const taken = new Set(chosen.map((c) => c.toLowerCase()));
  const hits = all.filter((s) => !taken.has(s.toLowerCase()) && s.toLowerCase() !== q && (!q || s.toLowerCase().includes(q)));
  return hits.sort((a, b) => Number(!a.toLowerCase().startsWith(q)) - Number(!b.toLowerCase().startsWith(q)) || a.localeCompare(b)).slice(0, max);
}

/** The edit form of one class: what is typed (empty names = the derived ones). */
export interface CatalogForm {
  displayName: string; displayPlural: string; description: string; domain: string; tags: string[]; tagText: string;
  steward: string; sensitivity: string; markChecked: boolean;
  /** The pinned system's code; empty = as detected from the data. */
  system: string;
}

/** The form for an entry: a name equal to the derived one is left empty (the placeholder shows it), so it keeps following the class. */
export function formOf(e: CatalogEntry): CatalogForm {
  const derived = e.displayName === e.derived.displayName;
  return {
    displayName: derived ? '' : e.displayName,
    displayPlural: derived && e.displayPlural === e.derived.displayPlural ? '' : e.displayPlural,
    description: e.description ?? '', domain: e.domain ?? '', tags: e.tags, tagText: '', steward: e.steward ?? '',
    sensitivity: e.sensitivity ?? '', markChecked: e.status === 'new', system: e.system ?? '',
  };
}

/** What is wrong with the form, by field (empty = nothing). */
export function formProblems(f: CatalogForm): Partial<Record<'displayName' | 'displayPlural' | 'description' | 'domain' | 'tags' | 'steward', string>> {
  const out: ReturnType<typeof formProblems> = {};
  const L = CATALOG_LIMITS;
  if (f.displayName.trim().length > L.name) out.displayName = `At most ${L.name} characters`;
  if (f.displayPlural.trim().length > L.name) out.displayPlural = `At most ${L.name} characters`;
  if (f.description.trim().length > L.description) out.description = `At most ${L.description} characters`;
  if (f.domain.trim().length > L.domain) out.domain = `At most ${L.domain} characters`;
  if (f.steward.trim().length > L.steward) out.steward = `At most ${L.steward} characters`;
  if (parseTags(f.tagText, f.tags).length > L.tags || f.tags.length > L.tags) out.tags = `At most ${L.tags} tags`;
  return out;
}

/**
 * The PATCH body of a form: every field (empty clears it; empty names go back to the derived ones), plus the status when
 * it is marked checked — which also accepts the suggestions for the domain, sensitivity and tags left empty.
 */
export function patchOf(f: CatalogForm, e: CatalogEntry) {
  const s = f.markChecked && e.status !== 'checked' ? e.suggestions : null;
  const tags = parseTags(f.tagText, f.tags);
  return {
    displayName: f.displayName.trim(), displayPlural: f.displayPlural.trim(), description: f.description.trim() || null,
    domain: f.domain.trim() || s?.domain || null, tags: tags.length ? tags : s?.tags ?? [], steward: f.steward.trim() || null,
    sensitivity: (f.sensitivity || s?.sensitivity || null) as Sensitivity | null, system: f.system || null,
    ...(f.markChecked && e.status !== 'checked' ? { status: 'checked' as const } : {}),
  };
}

/**
 * New data models of other environments that the environment looked at does not have, counted per environment in the
 * order given (only those with any): the sidebar and the banner say "+1 in Production" so they are not hidden.
 */
export function newElsewhere(entries: Iterable<CatalogEntry>, current: string | null | undefined, envIds: readonly string[]): { envId: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const e of entries) {
    if (e.status !== 'new' || e.inEnvironment) continue;
    for (const id of e.environments ?? []) if (id !== current) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return envIds.filter((id) => (counts.get(id) ?? 0) > 0).map((id) => ({ envId: id, count: counts.get(id)! }));
}
